import React, { useState, useEffect, useCallback } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css';
import Lottie from 'lottie-react';
import filaAnimation from '../assets/fila-de-espera-grub.json';
import boasVindasAnimation from '../assets/grub-boas-vindas.json';
import metaAnimation from '../assets/chegou-sua-vez.json';
import avaliacaoAnimation from '../assets/avialiacao-restaurante.json';
import emptyAnimation from '../assets/empty-relatory.json';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, User, Phone, Group, ChatLines, Clock, RefreshDouble, SendDiagonal } from 'iconoir-react';
import { entrarNaFila, consultarPosicaoFila, abandonarFila, getLojaInfo } from '../services/api';

const FilaAtendimento = ({ brand }) => {
  const navigate = useNavigate();
  const { hash } = useParams();

  // Estado do formulário
  const [formData, setFormData] = useState({
    nome_cliente: '',
    celular: '',
    quantidade_pessoas: '1',
    observacoes: ''
  });
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  // Estado de acompanhamento
  const [filaInfo, setFilaInfo] = useState(null);
  const [hashAtual, setHashAtual] = useState(hash || localStorage.getItem(`fila_hash_${brand}`) || '');
  const [carregando, setCarregando] = useState(false);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const [confirmandoSaida, setConfirmandoSaida] = useState(false);
  const [modalAbandonar, setModalAbandonar] = useState(false);
  const [confirmacaoTexto, setConfirmacaoTexto] = useState('');
  const [brandLogoUrl, setBrandLogoUrl] = useState(null);

  useEffect(() => {
    getLojaInfo(brand).then(res => {
      if (res?.loja?.logo_url) setBrandLogoUrl(res.loja.logo_url);
    }).catch(() => {});
  }, [brand]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErro('');
  };

  const validar = () => {
    if (!formData.nome_cliente.trim() || formData.nome_cliente.trim().length < 2) return 'Informe seu nome (mínimo 2 caracteres)';
    if (!formData.quantidade_pessoas || parseInt(formData.quantidade_pessoas) < 1) return 'Informe a quantidade de pessoas';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const erroValidacao = validar();
    if (erroValidacao) {
      setErro(erroValidacao);
      return;
    }

    setEnviando(true);
    setErro('');

    try {
      const result = await entrarNaFila(brand, {
        ...formData,
        quantidade_pessoas: parseInt(formData.quantidade_pessoas)
      });

      if (result && result.success) {
        const novoHash = result.data.hash;
        setHashAtual(novoHash);
        localStorage.setItem(`fila_hash_${brand}`, novoHash);
        setFilaInfo(result.data);
        setUltimaAtualizacao(new Date());
      } else {
        setErro(result?.message || 'Erro ao entrar na fila. Tente novamente.');
      }
    } catch (error) {
      setErro('Erro ao entrar na fila. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  const consultarPosicao = useCallback(async () => {
    if (!hashAtual) return;
    setCarregando(true);
    try {
      const result = await consultarPosicaoFila(brand, hashAtual);
      if (result && result.success) {
        setFilaInfo(result.data);
        setUltimaAtualizacao(new Date());

        // Se já foi atendido ou desistiu, limpar hash local
        if (result.data.status === 'atendido' || result.data.status === 'desistiu') {
          localStorage.removeItem(`fila_hash_${brand}`);
        }
      } else {
        // Hash inválida ou expirada
        setFilaInfo(null);
        setHashAtual('');
        localStorage.removeItem(`fila_hash_${brand}`);
      }
    } catch (error) {
      // Silenciar erro de polling
    } finally {
      setCarregando(false);
    }
  }, [brand, hashAtual]);

  // Consultar posição ao montar e a cada 10 segundos
  useEffect(() => {
    if (hashAtual) {
      consultarPosicao();
      const interval = setInterval(consultarPosicao, 10000);
      return () => clearInterval(interval);
    }
  }, [hashAtual, consultarPosicao]);

  const handleSairDaFila = () => {
    setFilaInfo(null);
    setHashAtual('');
    localStorage.removeItem(`fila_hash_${brand}`);
    setFormData({ nome_cliente: '', celular: '', quantidade_pessoas: '1', observacoes: '' });
  };

  const handleAbandonarFila = async () => {
    setModalAbandonar(false);
    setConfirmacaoTexto('');
    try {
      const result = await abandonarFila(brand, hashAtual);
      console.log('abandonarFila result:', result, 'brand:', brand, 'hash:', hashAtual);
    } catch (e) {
      console.error('abandonarFila error:', e);
    }
    await consultarPosicao();
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'aguardando':
        return { cor: '#f59e0b', bg: '#fef3c7', icone: '⏳', texto: 'Aguardando' };
      case 'chamado':
        return { cor: '#10b981', bg: '#d1fae5', icone: '', texto: '' };
      case 'atendido':
        return { cor: '#6b7280', bg: '#f3f4f6', icone: '', texto: '' };
      case 'desistiu':
        return { cor: '#ef4444', bg: '#fee2e2', icone: '', texto: '' };
      default:
        return { cor: '#6b7280', bg: '#f3f4f6', icone: '❓', texto: status };
    }
  };

  // Tela de acompanhamento
  if (hashAtual && filaInfo) {
    const statusConfig = getStatusConfig(filaInfo.status);
    const isChamado = filaInfo.status === 'chamado';
    const isFinalizado = filaInfo.status === 'atendido' || filaInfo.status === 'desistiu';

    return (
      <div className="bg-light" style={{ backgroundColor: '#f3f4f6', minHeight: '100vh' }}>
        <div className="container py-3">
          <div className="row justify-content-center">
            <div className="col-12 col-md-8 col-lg-6">
              {/* Header */}
              <div className="mb-3 d-flex align-items-center" style={{ gap: '12px' }}>
                <button className="btn btn-link p-0 text-dark" onClick={() => navigate(-1)}>
                  <ArrowLeft width={24} height={24} />
                </button>
                <h5 className="mb-0" style={{ fontWeight: 700 }}>Fila de Atendimento</h5>
              </div>

              {/* Card de Status */}
              <div className="p-4 text-center" style={{
                background: isChamado ? '#fff' : '#fff',
                border: isChamado ? '2px solid #0d6efd' : '1px solid #e5e7eb',
                borderRadius: '16px',
                boxShadow: isChamado ? '0 4px 24px rgba(13,110,253,0.15)' : '0 2px 8px rgba(0,0,0,0.06)',
              }}>
                {/* Status */}
                <div style={{ fontSize: '48px', marginBottom: '8px' }}>
                  {!isChamado && !isFinalizado && (
                    <Lottie animationData={filaAnimation} loop style={{ width: 180, height: 180, margin: '0 auto' }} />
                  )}
                  {(isChamado || isFinalizado) && statusConfig.icone}
                </div>
                {(isChamado || isFinalizado) && (
                  <span className="badge mb-1" style={{
                    background: statusConfig.bg,
                    color: statusConfig.cor,
                    fontSize: '0.85rem',
                    padding: '6px 16px',
                    borderRadius: '20px',
                    border: `1px solid ${statusConfig.cor}30`
                  }}>
                    {statusConfig.texto}
                  </span>
                )}
                {filaInfo.status === 'aguardando' && (
                  <>
                    <h1 style={{ fontWeight: 800, fontSize: '1.5rem', color: '#1a1a1a', margin: '12px 0 6px' }}>
                      Você está na 
                      fila de espera.
                    </h1>
                    <p style={{ color: '#6b7280', fontSize: '0.95rem', margin: 0 }}>
                      Aguarde, em breve iremos chamar você.
                    </p>
                  </>
                )}

                {/* Posição */}
                {filaInfo.status === 'aguardando' && (
                  <>
                    <div style={{ position: 'relative', width: '120px', height: '120px', margin: '16px auto 8px' }}>
                      <svg width="120" height="120" style={{ position: 'absolute', top: 0, left: 0 }}>
                        <circle cx="60" cy="60" r="52" fill="none" stroke="#e5e7eb" strokeWidth="6" />
                        <circle
                          cx="60" cy="60" r="52"
                          fill="none"
                          stroke="#f59e0b"
                          strokeWidth="6"
                          strokeLinecap="round"
                          strokeDasharray="80 246"
                          style={{ transformOrigin: '60px 60px', animation: 'rodar 1.6s linear infinite' }}
                        />
                      </svg>
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontWeight: 800, fontSize: '2.2rem', color: '#1a1a1a', lineHeight: 1 }}>
                          {filaInfo.posicao_atual}º
                        </span>
                        <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>na fila</span>
                      </div>
                    </div>
                    {filaInfo.pessoas_na_frente > 0 ? (
                      <p style={{ color: '#9ca3af', fontSize: '0.85rem', margin: '4px 0 0' }}>
                        {filaInfo.pessoas_na_frente} {filaInfo.pessoas_na_frente === 1 ? 'pessoa' : 'pessoas'} na sua frente
                      </p>
                    ) : (
                      <p style={{ color: '#10b981', fontSize: '0.85rem', fontWeight: 600, margin: '4px 0 0' }}>
                        Quase lá!
                      </p>
                    )}
                  </>
                )}

                {isChamado && (
                  <div className="mt-2">
                    <Lottie animationData={metaAnimation} loop style={{ width: 250, height: 250, margin: '0 auto' }} />
                    <h4 style={{ fontWeight: 800, fontSize: '2rem', color: '#0d6efd', animation: 'textPulse 1.5s ease-in-out infinite' }}>
                      Chegou o seu momento
                    </h4>
                    <p style={{ color: '#374151', fontSize: '0.95rem' }}>
                      A espera terminou e sua vez chegou. <br>
                      </br>Vá até o atendimento para ser atendido/a.
                    </p>
                    <div className="d-flex justify-content-center mt-3">
                      <img src={require('../assets/powered-by-grub_2.png')} alt="Powered by Grub" style={{ height: '34px', opacity: 0.7 }} />
                    </div>
                  </div>
                )}

                {isFinalizado && (
                  <div className="mt-2">
                    {filaInfo.status === 'atendido' ? (
                    <>
  <Lottie animationData={avaliacaoAnimation} loop style={{ width: 250, height: 250, margin: '0 auto' }} />
  <h4 style={{ fontWeight: 800, color: '#1a1a1a' }}>
    Tudo pronto. <br></br>Agora é só aproveitar.
  </h4>
  <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>
    Fique à vontade para curtir o momento, pode até deixar o celular de lado. 
    Depois, conta pra gente como foi.  <strong>Sua opinião é muito importante.</strong>
  </p>
</>
                    ) : (
                      <>
                        <Lottie animationData={emptyAnimation} loop style={{ width: 160, height: 160, margin: '0 auto' }} />
                        <h4 style={{ fontWeight: 800, color: '#1a1a1a' }}>Parece que você saiu da fila</h4>
                        <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>Não se preocupe, acontece! Se quiser, é só entrar novamente e a gente te coloca na fila em segundos.</p>
                      </>
                    )}
                  </div>
                )}

                {/* Info do cliente */}
                <div className="mt-3 p-3" style={{ background: '#f9fafb', borderRadius: '10px', textAlign: 'left' }}>
                  <div className="mb-1"><strong>Nome:</strong> {filaInfo.nome_cliente}</div>
                  <div><strong>Pessoas:</strong> {filaInfo.quantidade_pessoas}</div>
                </div>

                {/* Última atualização */}
                {ultimaAtualizacao && (
                  <div className="mt-3 d-flex align-items-center justify-content-center" style={{ gap: '6px', color: '#9ca3af', fontSize: '0.8rem' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981', display: 'inline-block', animation: 'livePulse 1.5s ease-in-out infinite' }} />
                    Atualizado às {ultimaAtualizacao.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    <button
                      className="btn btn-link p-0 ms-1"
                      onClick={consultarPosicao}
                      disabled={carregando}
                      style={{ color: '#6b7280' }}
                    >
                      <RefreshDouble width={16} height={16} className={carregando ? 'spin' : ''} />
                    </button>
                  </div>
                )}
              </div>

              {/* Ações */}
              <div className="mt-3 d-grid gap-2">
                {filaInfo.status === 'desistiu' ? (
                  <>
                    <button className="btn" onClick={handleSairDaFila} style={{ borderRadius: '8px', padding: '12px', fontWeight: 600, backgroundColor: '#0039ff', color: '#fff', border: 'none' }}>
                      Entrar na Fila Novamente
                    </button>
                    <Link to="/" className="btn btn-outline-secondary" style={{ borderRadius: '8px', padding: '10px', fontWeight: 500 }}>
                      Ver o Cardápio
                    </Link>
                  </>
                ) : (
                  <>
                    <Link to="/" className="btn" style={{ borderRadius: '8px', padding: '12px', fontWeight: 600, backgroundColor: '#0039ff', color: '#fff', border: 'none' }}>
                      Ver o Cardápio
                    </Link>
                    {isFinalizado && (
                      <button className="btn btn-outline-secondary" onClick={handleSairDaFila} style={{ borderRadius: '8px', padding: '10px', fontWeight: 500 }}>
                        Entrar na Fila Novamente
                      </button>
                    )}
                  </>
                )}
                {filaInfo.status === 'aguardando' && (
                  <button
                    className="btn btn-outline-danger"
                    onClick={() => setModalAbandonar(true)}
                    style={{ borderRadius: '8px', padding: '10px', fontWeight: 500 }}
                  >
                    Abandonar fila
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

              {/* Modal Abandonar Fila */}
              {modalAbandonar && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content" style={{ borderRadius: '16px', border: 'none' }}>
                      <div className="modal-body text-center p-4">
                        <div style={{ fontSize: '40px', marginBottom: '12px' }}>⚠️</div>
                        <h5 style={{ fontWeight: 700, marginBottom: '8px' }}>Abandonar a fila?</h5>
                        <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '16px' }}>
                          Você perderá sua posição e precisará entrar novamente.
                        </p>
                        <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '8px' }}>
                          Digite <strong>SAIR</strong> para confirmar:
                        </p>
                        <input
                          type="text"
                          className="form-control mb-3"
                          placeholder="Digite SAIR"
                          value={confirmacaoTexto}
                          onChange={(e) => setConfirmacaoTexto(e.target.value.toUpperCase())}
                          style={{ borderRadius: '8px', textAlign: 'center', fontWeight: 600 }}
                        />
                        <div className="d-grid gap-2">
                          <button
                            className="btn btn-danger"
                            onClick={handleAbandonarFila}
                            disabled={confirmacaoTexto !== 'SAIR'}
                            style={{ borderRadius: '8px', padding: '10px', fontWeight: 600 }}
                          >
                            Sim, quero sair
                          </button>
                          <button className="btn btn-outline-secondary" onClick={() => { setModalAbandonar(false); setConfirmacaoTexto(''); }} style={{ borderRadius: '8px', padding: '10px' }}>
                            Cancelar
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

        <style>{`
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.01); }
          }
          @keyframes textPulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.04); }
          }
          @keyframes livePulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.3; transform: scale(0.7); }
          }
          @keyframes rodar {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .spin {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Tela de formulário para entrar na fila
  return (
    <div className="bg-light" style={{ backgroundColor: '#f3f4f6', minHeight: '100vh' }}>
      <div className="container py-3">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">
            {/* Header */}
            <div className="mb-3 d-flex align-items-center" style={{ gap: '12px' }}>
              <button className="btn btn-link p-0 text-dark" onClick={() => navigate(-1)}>
                <ArrowLeft width={24} height={24} />
              </button>
              <h5 className="mb-0" style={{ fontWeight: 700 }}></h5>
            </div>

            {/* Header logos */}
            <div className="text-center mb-3">
              <div className="d-flex justify-content-center align-items-center mb-3" style={{ position: 'relative', width: '106px', height: '60px', margin: '0 auto 12px' }}>
                <img
                  src="https://grubicons.s3.us-east-1.amazonaws.com/logo-grub.png"
                  alt="Logo GRUB"
                  style={{ position: 'absolute', right: 0, width: '60px', height: '60px', borderRadius: '50%', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', zIndex: 1 }}
                />
                {brandLogoUrl && (
                  <img
                    src={`https://grubupload.s3.amazonaws.com/${brandLogoUrl}`}
                    alt="Logo da marca"
                    style={{ position: 'absolute', left: 0, width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
              </div>
              <h1 style={{ fontWeight: 800, fontSize: '1.5rem', color: '#1a1a1a' }}>Entrar na fila de atendimento</h1>
              <p style={{ color: '#6b7280', fontSize: '0.95rem', margin: 0 }}>Cadastre seus dados e acompanhe sua posição em tempo real.</p>
            </div>

            {/* Form */}
            <div className="p-3" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
              {erro && (
                <div className="alert alert-danger py-2 px-3" style={{ fontSize: '0.9rem', borderRadius: '8px' }}>
                  {erro}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label small fw-bold text-muted d-block text-start">
                  
                    Titular da reserva
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="nome_cliente"
                    value={formData.nome_cliente}
                    onChange={handleChange}
                    placeholder="Nome completo"
                    required
                    style={{ borderRadius: '8px' }}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-bold text-muted d-block text-start">
               
                    Celular
                  </label>
                  <PhoneInput
                    country="br"
                    value={formData.celular}
                    onChange={(value) => setFormData(prev => ({ ...prev, celular: value }))}
                    inputClass="phone-input-field"
                    buttonClass="phone-input-btn"
                    preferredCountries={['br', 'us', 'ar', 'pt']}
                    enableSearch
                    searchPlaceholder="Buscar país..."
                    searchNotFound="País não encontrado"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-bold text-muted d-block text-start">
                   
                    Quantidade de Pessoas
                  </label>
                  <select
                    className="form-select"
                    name="quantidade_pessoas"
                    value={formData.quantidade_pessoas}
                    onChange={handleChange}
                    required
                    style={{ borderRadius: '8px' }}
                  >
                    {Array.from({ length: 50 }, (_, i) => i + 1).map(n => (
                      <option key={n} value={n}>{n} {n === 1 ? 'pessoa' : 'pessoas'}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-bold text-muted d-block text-start">
                   
                    Observações
                  </label>
                  <textarea
                    className="form-control"
                    name="observacoes"
                    value={formData.observacoes}
                    onChange={handleChange}
                    rows="2"
                    maxLength={500}
                    placeholder="Alguma preferência? (opcional)"
                    style={{ borderRadius: '8px' }}
                  />
                </div>

                <button
                  type="submit"
                  className="btn w-100"
                  disabled={enviando}
                  style={{ borderRadius: '8px', padding: '12px', fontWeight: 600, backgroundColor: '#0039ff', color: '#fff', border: 'none' }}
                >
                  {enviando ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span>Entrando na fila...</>
                  ) : (
                    <><SendDiagonal width={16} height={16} className="me-2" />Entrar na Fila</>
                  )}
                </button>
                <div className="d-flex justify-content-center mt-3">
                  <img src={require('../assets/powered-by-grub_2.png')} alt="Powered by Grub" style={{ height: '34px' }} />
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilaAtendimento;
